export type ChartColor = "accent" | "accent-2" | "muted";

export interface ChartPoint {
  fecha: string;
  valor: number;
}

export interface ChartSeries {
  nombre: string;
  color: ChartColor;
  puntos: ChartPoint[];
}

export interface ChartProps {
  series: ChartSeries[];
  /** Zona horaria IANA usada para las etiquetas del eje horizontal. */
  tz: string;
  /** Alto del SVG en unidades CSS. */
  alto?: number;
  vacio?: string;
  cargando?: boolean;
  titulo: string;
  descripcion: string;
}

export const CHART = {
  width: 720,
  top: 16,
  right: 16,
  bottom: 34,
  left: 44,
} as const;

function instanteDe(fecha: string) {
  // Las RPCs devuelven días ya cortados en la tz pedida. El mediodía evita que
  // un `date` sin hora retroceda de día al formatearlo en América.
  return Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(fecha) ? `${fecha}T12:00:00Z` : fecha);
}

export function seriesConPuntosValidos(series: ChartSeries[]) {
  return series
    .map((serie) => ({
      ...serie,
      puntos: serie.puntos
        .filter((punto) => Number.isFinite(punto.valor) && !Number.isNaN(instanteDe(punto.fecha)))
        .sort((a, b) => instanteDe(a.fecha) - instanteDe(b.fecha)),
    }))
    .filter((serie) => serie.puntos.length > 0);
}

export function fechasDe(series: ChartSeries[]) {
  return [...new Set(series.flatMap((serie) => serie.puntos.map((punto) => punto.fecha)))].sort(
    (a, b) => instanteDe(a) - instanteDe(b),
  );
}

export function maximoDe(series: ChartSeries[]) {
  return Math.max(0, ...series.flatMap((serie) => serie.puntos.map((punto) => punto.valor)));
}

export function escalaY(valor: number, maximo: number, alto: number) {
  const base = alto - CHART.bottom;
  if (maximo <= 0) return base;
  return CHART.top + (1 - Math.max(0, valor) / maximo) * (base - CHART.top);
}

export function etiquetaFecha(fecha: string, tz: string) {
  try {
    return new Intl.DateTimeFormat("es-AR", {
      day: "numeric",
      month: "short",
      timeZone: tz,
    }).format(new Date(instanteDe(fecha)));
  } catch {
    return fecha;
  }
}

export function indicesEtiquetas(total: number) {
  if (total <= 4) return Array.from({ length: total }, (_, i) => i);
  return [...new Set([0, Math.round((total - 1) / 3), Math.round(((total - 1) * 2) / 3), total - 1])];
}
